/**
 * Fetch chatbot_log rows for a single setting with filters + pagination.
 * No external state library — plain useState/useEffect with abort + reload key.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import chatbotApi from "../../../api/chatbotApi";

const PAGE_SIZE = 50;

export const useBotLogs = (settingId, filters = {}) => {
  const accountId = useSelector((state) => state.auth.user?.accountId);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const { status, dateFrom, dateTo, search } = filters;
  // Stable filter signature to trigger reload when any filter changes.
  const filterKey = JSON.stringify({
    status: Array.isArray(status) ? status.slice().sort() : status,
    dateFrom,
    dateTo,
    search,
  });

  const fetchPage = useCallback(
    async ({ append = false } = {}) => {
      if (!settingId) {
        setRows([]);
        setTotal(0);
        setOffset(0);
        return;
      }

      const reqId = ++requestIdRef.current;
      const nextOffset = append ? offset + PAGE_SIZE : 0;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const res = await chatbotApi.getLogsBySetting(settingId, {
          accountId,
          status,
          dateFrom,
          dateTo,
          search,
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
        if (reqId !== requestIdRef.current) return; // stale
        if (res.status !== 1) {
          throw new Error(res.message || "Không tải được tin nhắn");
        }
        setRows((prev) => (append ? [...prev, ...(res.data || [])] : res.data || []));
        setTotal(res.total || 0);
        setOffset(nextOffset);
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        setError(err.message || String(err));
      } finally {
        if (reqId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    // We deliberately exclude `offset` from deps — its only consumer is the
    // `append` branch which reads the current value at call-time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingId, filterKey, accountId],
  );

  useEffect(() => {
    fetchPage({ append: false });
  }, [fetchPage]);

  const loadMore = () => fetchPage({ append: true });
  const refresh = () => fetchPage({ append: false });
  const hasMore = offset + PAGE_SIZE < total;

  return {
    rows,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
    pageSize: PAGE_SIZE,
  };
};
